import type { DocPageContent } from '../docs-content'

const page = {
  order: 15,
  slug: 'keyframes',
  title: '用关键帧制作动画',
  description:
    '使用关键帧、缓动与曲线编辑器，为变换、裁剪、文本、特效和音频的数值制作动画。',
  category: '创意工具',
  related: ['motion', 'motion-library', 'properties', 'effects-color'],
  sections: [
    {
      title: '选择在哪里编辑关键帧',
      blocks: [
        {
          kind: 'paragraph',
          text: '编辑（Edit）视图能让关键帧工作保持紧凑。运动（Motion）视图则提供了完整的图层轨迹表与曲线图，用于更深入的时序与曲线处理。',
        },
        {
          kind: 'table',
          headers: ['视图', '关键帧工作流'],
          rows: [
            [
              '编辑',
              '选中一个片段并按 `K` 为其活动属性添加关键帧。打开属性中的**动画**可查看预设与已应用状态。',
            ],
            [
              '运动',
              '展开一个图层以查看属性行、关键帧菱形标记、分段缓动以及内联数值曲线图。',
            ],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '在运动中，共享的播放头、选区、吸附、标尺、属性轨道与曲线图都使用同一时间轴。',
        },
      ],
    },
    {
      title: '添加关键帧',
      blocks: [
        {
          kind: 'steps',
          items: [
            '将播放头移动到你想要打关键帧的帧。',
            '在参数旁输入一个数值并按 `Enter`，或点击参数行上的**菱形**标记。',
            '移动到另一帧并设置第二个值，以在它们之间创建运动。',
            '点击计时器图标开启**自动关键帧**，或按 `A`，即可自动记录后续的数值变化。',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '你无法在转场区域内添加关键帧，也无法在播放头位于片段范围之外时添加——那里的菱形标记会被禁用。',
        },
      ],
    },
    {
      title: '你可以为哪些内容制作动画',
      blocks: [
        {
          kind: 'table',
          headers: ['片段类型', '可动画化数值'],
          rows: [
            ['所有可视片段', 'X / Y 位置、宽度、高度、旋转、不透明度、圆角'],
            ['视频', '增加锚点、四条裁剪边、裁剪柔化以及音量'],
            ['文本', '增加预设缩放、字体大小、行高、内边距、背景圆角、阴影与描边'],
            ['音频', '以分贝表示的音量'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '特效也会贡献自身可动画化的参数——留意每个滑块旁的关键帧开关。',
        },
      ],
    },
    {
      title: '塑造运动',
      blocks: [
        {
          kind: 'list',
          items: [
            '点击两个关键帧之间的**连接线**以打开**缓动编辑器**。',
            '在**三次缓动**与**弹簧**下浏览预设曲线。按**全部**、**入**、**出**或**入出**筛选。',
            '选择**保持**以保留较早的值，直到下一个关键帧。',
            '切换到**编辑**以直接塑造曲线：拖动曲线图上的两个控制手柄，或输入精确数值。弹簧会暴露**张力**、**摩擦**和**质量**，并可预览其真实的回弹效果。',
            '在实时预览中感受**位置**、**缩放**、**旋转**或**不透明度**的时序，并**暂停**循环。',
            '用**另存为**将调整过的曲线保存为**自定义预设**，以后可**更新**它，或**重置**回它原本来自的预设。自定义预设存储在你的设备上，并在每个项目中都可用。',
            '复制、剪切、粘贴和删除关键帧，用框选选择分组，拖动以重新调整时序，并按 `Alt` 拖动进行复制。',
          ],
        },
      ],
    },
    {
      title: '在数值曲线图中操作',
      blocks: [
        {
          kind: 'list',
          items: [
            '从下拉菜单中选择一个属性来编辑其曲线，或保持“全部”以将其他曲线作为淡化的叠加曲线查看。活动属性的关键帧数量会显示在旁边。',
            '曲线图会绘制带标签的**网格**——横向为帧或秒，纵向为数值——方便你一眼读出位置。',
            '用**适配内容**框定曲线，或用**放大** / **缩小**与鼠标滚轮微调视图。**重置视图**会回到默认的适配状态。',
            '用**上一个** / **下一个**箭头逐个查看关键帧，然后为所选关键帧输入精确的帧（**F**）与数值（**V**）。',
            '拖动关键帧时，按住 **Shift** 可将其锁定到单一轴，按住 **Alt** 可进行微调。',
          ],
        },
      ],
    },
    {
      title: '转换并清理生成的动画',
      blocks: [
        {
          kind: 'paragraph',
          text: '漂移、呼吸、抖动、旋转等实时行为会在播放期间求值，而无需创建属性关键帧。',
        },
        {
          kind: 'list',
          items: [
            '选择**烘焙为关键帧**可将实时图层行为转换为可编辑的关键帧。',
            '使用**已应用**可跳转到手动关键帧或某个生成式预设的首帧。',
            '当片段修剪导致已存储的关键帧落在可见时长之外时，使用**修剪动画**。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '参阅 [应用并复用动画](motion-library)，了解预设、实时行为、文本运动、烘焙以及已保存的动画。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
